// Geteilte Typen fuer Canvas-Objekte und Links.
//
// Visuelle Primitive  : sticky, text, shape-rect
// Semantische Typen   : requirement, pain, dependency  (Plan §S5.3, FR-018)
// Strukturell         : block (Method-Container), region (Cell innerhalb eines Blocks)
// Verbindungen        : Link (relates-to / resolves / depends-on) — lebt in Y.Array, NICHT in Y.Map
//
// Zod-Schemas sind die Source of Truth fuer Validierung (Inspector, Method-Scaffold,
// Export). Fuer den Hot-Path (Y.Map.observe Render-Loop) gibt es `isCanvasObject`
// als schneller typeof-basierter Guard.

import { z } from "zod";
import { OBJECT_META } from "./object-meta.js";
import { ShapeKindSchema, ShapeSemanticsSchema } from "./shape-kind.js";

// ---------- Common Base ----------

const BaseObjectSchema = z.object({
  id: z.string().min(1),
  x: z.number(),
  y: z.number(),
  createdBy: z.string(),
  createdAt: z.number(),
  /** Region in der das Objekt verankert ist; null = frei auf dem Canvas. */
  parentRegionId: z.string().nullable().default(null),
  /** Optionales Lock: blockiert Move/Resize/Delete/Edit (V2 Slice 4 — Schema in Slice 1). */
  locked: z.boolean().optional(),
  /** Optionale Z-Reihenfolge: Render-Sort `(zOrder ?? 0, createdAt)`. V2 Slice 3. */
  zOrder: z.number().optional(),
  /** V2 Slice 5: Lightweight-Group — Selection expandiert auf alle Objekte mit gleicher groupId. */
  groupId: z.string().optional(),
  /** V2 Slice 5: Frame-Verankerung — Frame-Move zieht parentFrameId-Children mit (Cascade). */
  parentFrameId: z.string().optional(),
});

// ---------- Visuelle Primitive ----------

export const StickyColorSchema = z.enum([
  "yellow",
  "pink",
  "mint",
  "blue",
  "lavender",
  "peach",
  "gray",
  "white",
]);
export type StickyColor = z.infer<typeof StickyColorSchema>;

export const StickyObjSchema = BaseObjectSchema.extend({
  type: z.literal("sticky"),
  text: z.string(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  /** V2 Slice 5: 8 Farb-Presets statt nur Gelb. Default-Render (kein color) = yellow. */
  color: StickyColorSchema.optional(),
});
export type StickyObj = z.infer<typeof StickyObjSchema>;

export const TextObjSchema = BaseObjectSchema.extend({
  type: z.literal("text"),
  text: z.string(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
});
export type TextObj = z.infer<typeof TextObjSchema>;

export const ShapeRectObjSchema = BaseObjectSchema.extend({
  type: z.literal("shape-rect"),
  width: z.number().positive(),
  height: z.number().positive(),
  label: z.string(),
});
export type ShapeRectObj = z.infer<typeof ShapeRectObjSchema>;

export const MindmapNodeObjSchema = BaseObjectSchema.extend({
  type: z.literal("mindmap-node"),
  text: z.string(),
  /** Tree-Edge: Single Source of Truth. tree-parent-Link wird daraus synchronisiert. */
  parentNodeId: z.string().nullable().default(null),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
  /** Optionaler Color-Override gegen die automatische Level-Farbe. */
  accent: z.string().optional(),
});
export type MindmapNodeObj = z.infer<typeof MindmapNodeObjSchema>;

export const ShapeObjSchema = BaseObjectSchema.extend({
  type: z.literal("shape"),
  kind: ShapeKindSchema,
  width: z.number().positive(),
  height: z.number().positive(),
  text: z.string(),
  semantics: ShapeSemanticsSchema.optional(),
  accent: z.string().optional(),
});
export type ShapeObj = z.infer<typeof ShapeObjSchema>;

export const IconObjSchema = BaseObjectSchema.extend({
  type: z.literal("icon"),
  iconName: z.string().min(1),
  width: z.number().positive(),
  height: z.number().positive(),
  color: z.string().optional(),
});
export type IconObj = z.infer<typeof IconObjSchema>;

export const ImageObjSchema = BaseObjectSchema.extend({
  type: z.literal("image"),
  /** Referenz auf das im Object-Store (S3/MinIO) abgelegte Bild; Bytes leben NICHT in Yjs. */
  assetId: z.string().min(1),
  width: z.number().positive(),
  height: z.number().positive(),
  /** Urspruenglicher Dateiname, nur fuer Anzeige/Download. */
  originalName: z.string().optional(),
});
export type ImageObj = z.infer<typeof ImageObjSchema>;

// ---------- Semantische Enums ----------

export const PrioritySchema = z.enum(["must", "should", "could", "wont"]);
export type Priority = z.infer<typeof PrioritySchema>;

export const RequirementCategorySchema = z.enum([
  "functional",
  "non-functional",
  "data",
  "out-of-scope",
]);
export type RequirementCategory = z.infer<typeof RequirementCategorySchema>;

export const SeveritySchema = z.enum(["low", "med", "high"]);
export type Severity = z.infer<typeof SeveritySchema>;

export const DependencyKindSchema = z.enum(["internal", "external", "org"]);
export type DependencyKind = z.infer<typeof DependencyKindSchema>;

export const DependencyStatusSchema = z.enum(["open", "confirmed", "at-risk"]);
export type DependencyStatus = z.infer<typeof DependencyStatusSchema>;

// ---------- Semantische Objekte ----------

export const RequirementObjSchema = BaseObjectSchema.extend({
  type: z.literal("requirement"),
  text: z.string().min(1),
  priority: PrioritySchema,
  category: RequirementCategorySchema,
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
});
export type RequirementObj = z.infer<typeof RequirementObjSchema>;

export const PainObjSchema = BaseObjectSchema.extend({
  type: z.literal("pain"),
  text: z.string().min(1),
  severity: SeveritySchema,
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
});
export type PainObj = z.infer<typeof PainObjSchema>;

export const DependencyObjSchema = BaseObjectSchema.extend({
  type: z.literal("dependency"),
  text: z.string().min(1),
  kind: DependencyKindSchema,
  status: DependencyStatusSchema,
  /** Optionaler Severity-Tag fuer pain-dependency-Methode (Welle 2). */
  severity: SeveritySchema.optional(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
});
export type DependencyObj = z.infer<typeof DependencyObjSchema>;

export type SemanticObj = RequirementObj | PainObj | DependencyObj;
export const SEMANTIC_TYPES = ["requirement", "pain", "dependency"] as const;
export type SemanticType = (typeof SEMANTIC_TYPES)[number];

// ---------- Block + Region (Method-Container) ----------

export const BlockObjSchema = BaseObjectSchema.extend({
  type: z.literal("block"),
  /** Referenz in den METHOD_CATALOG. */
  methodId: z.string().min(1),
  width: z.number().positive(),
  height: z.number().positive(),
  label: z.string().min(1),
});
export type BlockObj = z.infer<typeof BlockObjSchema>;

export const RegionAcceptsSchema = z.enum([
  "any",
  "sticky",
  "text",
  "requirement",
  "pain",
  "dependency",
]);
export type RegionAccepts = z.infer<typeof RegionAcceptsSchema>;

// ---------- Frame (V2 Slice 5) ----------

export const FrameObjSchema = BaseObjectSchema.extend({
  type: z.literal("frame"),
  title: z.string(),
  width: z.number().positive(),
  height: z.number().positive(),
  /** Optionaler CSS-Farbhinweis fuer Frame-Akzent (Border-Color etc). */
  accent: z.string().optional(),
});
export type FrameObj = z.infer<typeof FrameObjSchema>;

export const RegionObjSchema = BaseObjectSchema.extend({
  type: z.literal("region"),
  blockId: z.string().min(1),
  width: z.number().positive(),
  height: z.number().positive(),
  label: z.string(),
  /** Optionaler CSS-Farbhinweis (HSL/Hex), z.B. fuer MoSCoW-Quadranten. */
  accent: z.string().optional(),
  /** Welcher Typ darf hier abgelegt werden? Default "any". */
  accepts: RegionAcceptsSchema.default("any"),
  /** Mapping auf ein semantisches Feld (z.B. priority=must), gefuellt beim Drop. */
  semanticHint: z.record(z.string(), z.string()).optional(),
});
export type RegionObj = z.infer<typeof RegionObjSchema>;

// ---------- Discriminated Union ----------

export const CanvasObjectSchema = z.discriminatedUnion("type", [
  StickyObjSchema,
  TextObjSchema,
  ShapeRectObjSchema,
  RequirementObjSchema,
  PainObjSchema,
  DependencyObjSchema,
  BlockObjSchema,
  RegionObjSchema,
  FrameObjSchema,
  MindmapNodeObjSchema,
  ShapeObjSchema,
  IconObjSchema,
  ImageObjSchema,
]);
export type CanvasObject = z.infer<typeof CanvasObjectSchema>;
export type ObjectType = CanvasObject["type"];

// ---------- Link ----------

export const LinkTypeSchema = z.enum([
  "relates-to",
  "resolves",
  "depends-on",
  "tree-parent",
  "sequence-flow",
]);
export type LinkType = z.infer<typeof LinkTypeSchema>;

/** Verlaufs-Stil eines Connectors. Optional — Bestandsdaten ohne Feld rendern gerade. */
export const LinkRoutingSchema = z.enum(["straight", "curved", "orthogonal"]);
export type LinkRouting = z.infer<typeof LinkRoutingSchema>;

export const LinkSchema = z.object({
  id: z.string().min(1),
  from: z.string().min(1),
  to: z.string().min(1),
  type: LinkTypeSchema,
  routing: LinkRoutingSchema.optional(),
  createdBy: z.string(),
  createdAt: z.number(),
});
export type Link = z.infer<typeof LinkSchema>;

// ---------- Type Guards ----------

/**
 * Hot-Path Guard fuer Y.Map.observe. Vermeidet Zod-Parsing in jeder Render-Schleife.
 * Strenge Schema-Validierung passiert an Boundaries (Inspector-Input, Method-Scaffold).
 */
export function isCanvasObject(value: unknown): value is CanvasObject {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (
    typeof v.id !== "string" ||
    typeof v.x !== "number" ||
    typeof v.y !== "number" ||
    typeof v.createdBy !== "string" ||
    typeof v.createdAt !== "number"
  ) {
    return false;
  }
  switch (v.type) {
    case "sticky":
    case "text":
      return typeof v.text === "string";
    case "shape-rect":
      return (
        typeof v.width === "number" && typeof v.height === "number" && typeof v.label === "string"
      );
    case "requirement":
      return (
        typeof v.text === "string" &&
        typeof v.priority === "string" &&
        typeof v.category === "string"
      );
    case "pain":
      return typeof v.text === "string" && typeof v.severity === "string";
    case "dependency":
      return (
        typeof v.text === "string" && typeof v.kind === "string" && typeof v.status === "string"
      );
    case "block":
      return (
        typeof v.methodId === "string" &&
        typeof v.width === "number" &&
        typeof v.height === "number" &&
        typeof v.label === "string"
      );
    case "region":
      return (
        typeof v.blockId === "string" &&
        typeof v.width === "number" &&
        typeof v.height === "number" &&
        typeof v.label === "string"
      );
    case "frame":
      return (
        typeof v.title === "string" && typeof v.width === "number" && typeof v.height === "number"
      );
    case "mindmap-node":
      return (
        typeof v.text === "string" &&
        (v.parentNodeId === null || typeof v.parentNodeId === "string")
      );
    case "shape":
      return (
        typeof v.kind === "string" &&
        typeof v.width === "number" &&
        typeof v.height === "number" &&
        typeof v.text === "string"
      );
    case "icon":
      return (
        typeof v.iconName === "string" &&
        typeof v.width === "number" &&
        typeof v.height === "number"
      );
    case "image":
      return (
        typeof v.assetId === "string" && typeof v.width === "number" && typeof v.height === "number"
      );
    default:
      return false;
  }
}

export function isSemanticObject(value: unknown): value is SemanticObj {
  if (!isCanvasObject(value)) return false;
  // Registry-Migration: "semantic" Flag lebt in OBJECT_META.
  return OBJECT_META[value.type].isSemantic;
}

export function isLink(value: unknown): value is Link {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === "string" &&
    typeof v.from === "string" &&
    typeof v.to === "string" &&
    typeof v.type === "string" &&
    typeof v.createdBy === "string" &&
    typeof v.createdAt === "number"
  );
}

/** Strenge Zod-Validierung — fuer Boundary-Eingaben (Inspector). */
export function parseCanvasObject(value: unknown): CanvasObject | null {
  const result = CanvasObjectSchema.safeParse(value);
  return result.success ? result.data : null;
}

export function parseLink(value: unknown): Link | null {
  const result = LinkSchema.safeParse(value);
  return result.success ? result.data : null;
}
